import React, { ReactNode, useEffect, useState } from 'react';
import { Stack } from '@mui/system';

interface StickyWrapperProps {
  children: ReactNode;
  initialBottomOffset?: number; // Default offset when no keyboard is visible
  visible: boolean;
}

export const StickyWrapper: React.FC<StickyWrapperProps> = ({
  children,
  initialBottomOffset = 0,
  visible = true,
}) => {
  const [bottomOffset, setBottomOffset] = useState<number>(initialBottomOffset); // Default distance from the bottom

  useEffect(() => {
    const updateBottomOffset = () => {
      const viewport = window.visualViewport;

      if (viewport) {
        const keyboardHeight =
          window.innerHeight - viewport.height - viewport.offsetTop;
        const offset = keyboardHeight > 0 ? keyboardHeight : 0;
        setBottomOffset(initialBottomOffset + offset);
      }
    };

    const handleBlur = () => {
      // Reset to initial position when input loses focus
      setBottomOffset(initialBottomOffset);
    };

    const handleScroll = () => {
      const activeElement = document.activeElement as HTMLElement;

      if (activeElement && activeElement.tagName === 'INPUT') {
        activeElement.blur(); // Remove focus from the input field
        setBottomOffset(initialBottomOffset);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateBottomOffset);
    }

    // Attach blur listener to all inputs
    window.addEventListener('focusout', handleBlur);
    window.addEventListener('wheel', handleScroll); // Add wheel listener for ios and mouse
    window.addEventListener('touchmove', handleScroll); // Add touchmove listener for android

    // Initial adjustment
    updateBottomOffset();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateBottomOffset);
      }
      window.removeEventListener('focusout', handleBlur);
      window.removeEventListener('touchmove', handleScroll);
      window.removeEventListener('wheel', handleScroll);
    };
  }, [initialBottomOffset]);

  return (
    <Stack
      alignItems="center"
      p={2.5}
      visibility={visible ? 'visible' : 'hidden'}
      position="fixed"
      left={0}
      bottom={bottomOffset}
      width="100%"
      sx={{
        transition: 'bottom 0.1s ease-in-out', // Faster transition
      }}
    >
      {children}
    </Stack>
  );
};
